import type { Dictionary } from './he';

/**
 * Typed against the Hebrew key set. A key that exists there and not here is a
 * build error, and a key here that does not exist there is too, which is the
 * whole point: there is no fallback locale, so a missing string would render
 * as nothing at all.
 */
export const en: Dictionary = {
  /* Identity and navigation */
  appName: 'Magbit',
  appSubtitle: 'Private lesson management',
  tagline: 'Less admin. More teaching.',
  navHome: 'Home',
  navPricing: 'Pricing',
  navPremium: 'Premium',
  navTerms: 'Terms',
  navPrivacy: 'Privacy',
  navRefunds: 'Refunds',
  navApp: 'Open the app',
  navDashboard: 'Overview',
  navStudents: 'Students',
  navLessons: 'Lessons',
  navCalendar: 'Calendar',
  navPayments: 'Payments',
  navReceipts: 'Receipts',
  navReceiptReports: 'Receipt reports',
  navAdmin: 'Admin',
  navReports: 'Reports',
  navProfile: 'Settings',
  navBackup: 'Backup',
  navLogout: 'Sign out',
  openMenu: 'Open menu',

  /* Shared actions */
  save: 'Save',
  cancel: 'Cancel',
  close: 'Close',
  delete: 'Delete',
  edit: 'Edit',
  add: 'Add',
  search: 'Search',
  confirm: 'Confirm',
  back: 'Back',
  retry: 'Try again',
  loading: 'Loading',
  copy: 'Copy',
  copied: 'Copied',
  share: 'Share',
  print: 'Print',

  /* Lesson status */
  paid: 'Paid',
  unpaid: 'Unpaid',
  scheduled: 'Scheduled',
  stCancelledCharged: 'Cancelled, charged',
  stCancelledFree: 'Cancelled, not charged',
  stPaidDesc: 'The money arrived',
  stUnpaidDesc: 'A charge awaiting payment',
  stCancelledChargedDesc: 'Cancelled and still billed',
  stCancelledFreeDesc: 'Cancelled and not billed',
  stPostpone: 'Postpone',
  stPostponeDesc: 'Move to a new date and set it back to unpaid',
  statusMenuTitle: 'Lesson status',
  statusChangedTo: 'Status changed to {0}',
  statusChangeError: 'Could not change the status',

  /* Money and debt */
  debt: 'Outstanding',
  income: 'Income',
  amount: 'Amount',
  price: 'Price per lesson',
  currency: '₪',
  owedSince: 'Owed since {0}',

  /* Students */
  studentName: 'Student name',
  firstName: 'First name',
  lastName: 'Last name',
  phone: 'Phone',
  address: 'Address',
  entryCode: 'Entry code',
  notes: 'Notes',
  archived: 'Archived',
  noStudentsMatch: 'No students match the filter',
  noStudentsMatchHint: 'Reset the filters to see the whole roster',

  /* Roster filters */
  sortLabel: 'Sort',
  sortByName: 'Name A-Z',
  sortByLessons: 'Most lessons',
  sortByIncome: 'Total income',
  sortByDebt: 'Most debt',
  debtFilterLabel: 'Debt',
  debtFilterAll: 'All students',
  debtFilterWith: 'Owing',
  debtFilterWithout: 'Not owing',
  statusFilterLabel: 'Status',
  statusFilterActive: 'Active',
  statusFilterArchived: 'Archived',
  statusFilterBoth: 'Active and archived',
  rosterStudents: 'Students',
  rosterOwingCount: 'Students owing',
  rosterMarkedPaid: 'Marked paid',
  history: 'History',
  markAllPaid: 'Mark everything paid',
  markAllPaidTitle: "Mark {0}'s debt as paid",
  markAllPaidBody: 'Only lessons that have already happened and are still unpaid are marked. Future lessons are left alone.',
  addManyDates: 'Add several dates',
  addManyDatesSave: 'Add {0} lesson || Add {0} lessons',
  addManyDatesHint: "An empty time inherits the student's recurring hour for that weekday",
  addDate: 'Add date',
  remove: 'Remove',
  weekday: 'Day',
  durationMinutes: 'Minutes',
  contactsLocked: 'Contacts and payment messages are part of Premium',
  editorExtrasHint: 'Recurring hours and contacts open once the student is saved',
  absences: 'Known absence',
  absenceStart: 'From date',
  absenceEnd: 'To date',
  absenceNote: 'Note',
  absenceNoteEn: 'Note in English',
  bankNameEn: 'Bank in English',
  bankOwnerEn: 'Account holder in English',
  businessNameEn: 'Business name in English',
  businessAddressEn: 'Business address in English',
  signatureNameEn: 'Signature name in English',
  footerTextEn: 'Footer text in English',
  absenceNotePlaceholder: 'e.g. vacation, broken arm',
  slotConflict: 'This hour is already a recurring lesson with another student',

  noStudents: 'No students yet',
  noStudentsHint: 'Add a first student to get started',

  /* Time */
  weekdaySunday: 'Sunday',
  weekdayMonday: 'Monday',
  weekdayTuesday: 'Tuesday',
  weekdayWednesday: 'Wednesday',
  weekdayThursday: 'Thursday',
  weekdayFriday: 'Friday',
  weekdaySaturday: 'Saturday',
  duration: 'Duration',
  minutes: 'minutes',
  time: 'Time',
  date: 'Date',

  /* Premium */
  premiumTitle: 'Magbit Premium',
  premiumLocked: 'This feature is part of Premium',
  premiumUpgrade: 'Upgrade to Premium',
  premiumActive: 'Subscription active',
  premiumExpired: 'Subscription expired',

  /* Errors and empty states */
  errorTitle: 'Something went wrong',
  errorGeneric: 'That did not work. You can try again.',
  errorNetwork: 'No connection to the server',
  errorNotFound: 'Page not found',
  errorNotFoundHint: 'The link may have changed, or the page was removed',
  errorUnauthorized: 'Please sign in again',
  emptyTitle: 'Nothing here yet',

  /* Public documents */
  paymentRequestTitle: 'Payment request',
  paymentRequestInvalid: 'This link is not valid, or it has expired',
  intakeFormTitle: 'Joining form',
  intakeFormInvalid: 'This form is not available',
  intakeFormThanks: 'Got it. Thank you!',

  /* Marketing */
  marketingHeroLead: 'Magbit keeps students, lessons, payments and reminders in one place.',
  marketingCta: 'Open the app',
  pricingTitle: 'A plan that fits your business',

  /* Sign in */
  loginTitle: 'Magbit',
  loginLead: 'Sign in with Google to reach your students, lessons and payments.',
  loginWithGoogle: 'Sign in with Google',
  loginPrivacyMore: 'Read privacy policy',
  loginAbout: 'See what Magbit does',
  loginFailed: 'Sign-in did not work. You can try again.',

  /* Dashboard */
  scheduledIncome: 'Expected income',
  todayTitle: 'Today',
  todayLessons: "Today's lessons",
  todayNone: 'No lessons today',
  nextLessonTitle: 'Next lesson',
  nextLessonNone: 'Nothing scheduled',
  starterTitle: 'Three steps to get going',
  starterAddStudent: 'Add your first student',
  starterAddSlot: 'Set a recurring hour',
  starterAddLesson: 'Log a first lesson',
  collectedIncome: 'Income collected this year',
  lessonCount: 'Lessons',
  studentCount: 'Students',
  averageLesson: 'Average per lesson',
  dashboardSummary: 'Activity summary',
  dashboardAnalytics: 'Activity analysis',
  openDebtAlert: '{0} lesson with open debt || {0} lessons with open debt',
  openDebtHint: 'Open the payment list',
  debtStudentsTitle: 'Students with outstanding payments',
  debtLessonCount: '{0} unpaid lesson || {0} unpaid lessons',
  viewAllPayments: 'View all payments',
  paymentStatusChart: 'Payment status',
  paidDebtScheduled: 'Paid · debt · upcoming',
  monthlyIncomeChart: 'Monthly income',
  lastSixMonths: 'Last six months',
  topIncomeStudents: 'Top income students',
  topFive: 'Top five',
  lessonsByStudent: 'Lessons by student',
  navPaymentDocuments: 'Payment documents',

  /* Students */
  addStudent: 'Add a student',
  editStudentTitle: 'Edit card',
  showArchived: 'Show archived',
  openStudent: 'Full page',
  deleteStudentTitle: 'Delete {0}',
  deleteStudentBody: 'Deleting also removes every lesson, recurring hour, contact and availability for this student. This cannot be undone.',

  /* Lessons */
  addLesson: 'Add a lesson',
  filterAll: 'All',
  noLessons: 'No lessons to show',
  noLessonsHint: 'Add a lesson, or change the filter',
  paymentsSearchPlaceholder: 'Search by student name',
  paymentsFilterLabel: 'Filter payments',
  allStatuses: 'All statuses',
  resetFilters: 'Reset',
  printList: 'Print list',
  paymentsShowing: 'Showing {0} payment || Showing {0} payments',
  pageStatus: 'Page {0} of {1}',
  previousPage: 'Previous page',
  nextPage: 'Next page',
  editPaymentStatus: 'Change payment status',
  cancelledStatuses: 'Cancelled',
  postponeTitle: 'Postpone {0}',
  postponeHint: 'Pick a new date and time. The lesson goes back to unpaid.',
  editLesson: 'Edit lesson',
  editLessonDesc: 'Change the student, date, time or amount',
  deleteLessonDesc: 'Remove the lesson from the record entirely',
  clearAllLessons: 'Clear all',
  clearAllLessonsTitle: 'Delete every lesson',
  clearAllLessonsBody: 'Every lesson is deleted, including the ones the current filter is hiding. The income record is reset to nothing. This cannot be undone.',
  deleteLessonTitle: 'Delete {0} lesson',
  deleteLessonBody: 'The lesson is removed from the record and counts towards neither income nor debt. A cancelled lesson stays on the record; a deleted one does not. This cannot be undone.',

  /* Calendar */
  previousMonth: 'Previous month',
  nextMonth: 'Next month',
  calendarToday: 'Today',
  calendarMonth: 'Month',
  calendarWeek: 'Week',
  calendarDay: 'Day',
  calendarView: 'Calendar view',
  calendarPrevious: 'Previous period',
  calendarNext: 'Next period',
  calendarNoDayLessons: 'No lessons on this day',
  calendarNoLessonsShort: 'No lessons',
  calendarDayLoad: '{0} lesson || {0} lessons',
  calendarNoTime: 'No time',
  calendarPlanned: 'Recurring slot',

  /* Settings */
  accountTitle: 'Account',
  displayTitle: 'Display',
  languageLabel: 'Language',
  themeLabel: 'Theme',
  themeLight: 'Light',
  themeDark: 'Dark',
  paymentDetailsTitle: 'Payment details',
  payboxLink: 'PayBox link',
  bitLink: 'Bit link',
  bankName: 'Bank',
  bankBranch: 'Branch',
  bankAccount: 'Account number',
  bankOwner: 'Account holder',
  copyBankDetails: 'Copy bank details',
  sincerely: 'Best regards,',
  issuedOn: 'Issued on',
  savedNotice: 'Saved',

  /* Premium */
  premiumAdminAccess: 'Access comes from an admin role',
  premiumUntil: 'Valid until',
  premiumManageBilling: 'Manage billing',
  premiumFeaturesTitle: "What's included",
  featureExports: 'Excel and CSV exports',
  featureBranding: 'Your business on the receipts',
  featureReminders: 'Payment reminders and contacts',
  featureStudentProfile: 'The full student page',
  featureIntakeForms: 'A joining form for clients',

  /* Receipts */
  receiptTitle: 'Receipt',
  receiptDraft: 'Draft',
  receiptNumber: 'Receipt number',
  receiptFor: 'Issued to',
  total: 'Total',
  issueReceipt: 'Issue receipt',
  cancelReceipt: 'Cancel receipt',
  cancelReceiptTitle: 'Cancel receipt {0}',
  cancelReceiptBody: 'A cancelled receipt stays on record, and its number is never reused.',
  cancelReceiptReason: 'Reason for cancelling',
  receiptCancelled: 'This receipt was cancelled.',
  noReceipts: 'No receipts yet',
  noReceiptsHint: 'A receipt is written from lessons that were paid',
  unreportedTitle: 'Paid lessons with no receipt yet',
  writeReceipt: 'Write a receipt',
  bulkIssueAction: 'Issue receipts for every lesson',
  bulkIssueTitle: 'Issue {0} receipt || Issue {0} receipts',
  bulkIssueBody: 'A separate receipt for every lesson, numbered in the order the lessons happened. Every document is dated today, the day it is being produced, and carries the lesson day as the date the money arrived. The whole batch cannot be undone at once; each receipt can still be cancelled individually afterwards.',
  bulkIssueConfirm: 'Issue receipts',
  bulkIssueDone: '{0} receipt issued. || {0} receipts issued.',
  newReceipt: 'New receipt',
  editReceipt: 'Edit draft',
  duplicate: 'Duplicate',
  duplicateReceipt: 'Duplicate receipt',
  receiptIssued: 'Issued',
  receiptCancelledShort: 'Cancelled',
  receiptsShowing: 'Showing {0} receipt || Showing {0} receipts',
  receiptSearchPlaceholder: 'Search by name, number or description',
  noReceiptsMatch: 'No receipts match the filter',
  noReceiptsMatchHint: 'Reset the filters to see the whole list',
  deleteDraftTitle: 'Delete draft',
  deleteDraftBody: 'A draft has no receipt number, so deleting it leaves no gap in the numbering. This cannot be undone.',
  oneOffCustomer: 'One-off customer',
  customerName: 'Customer name',
  receiptDescription: 'Description',
  issueDate: 'Issue date',
  paymentDate: 'Payment date',
  paymentMethod: 'Payment method',
  paymentReference: 'Reference',
  allMethods: 'All payment methods',
  fromDate: 'From',
  toDate: 'To',
  minAmount: 'Min amount',
  maxAmount: 'Max amount',
  status: 'Status',
  methodCash: 'Cash',
  methodBankTransfer: 'Bank transfer',
  methodCreditCard: 'Credit card',
  methodBit: 'Bit',
  methodPaybox: 'Paybox',
  methodCheck: 'Cheque',
  methodOther: 'Other',

  /* Israeli compliance: what the law puts on the face of a document */
  docTypeReceipt: 'Receipt',
  docTypeTaxInvoiceReceipt: 'Tax invoice / receipt',
  docOriginal: 'Original',
  docCopy: 'Certified copy of the original',
  docComputerGenerated: 'Computer-generated document',
  docSoftwareRegistry: 'Software registration no. {0}',
  docVatExemptLabel: 'VAT-exempt business',
  docVatRegisteredLabel: 'VAT reg. no.',
  docCompanyLabel: 'Company no.',
  docAmutaLabel: 'Non-profit no.',
  docCancelledStamp: 'CANCELLED',
  receiptPaymentDetails: 'Payment details',
  receiptNetAmount: 'Amount before VAT',
  receiptVatLine: 'VAT {0}%',
  receiptGrossAmount: 'Total paid',
  chequeLine: 'Bank {0}, branch {1}, account {2}, cheque {3}',
  chequeDueDate: 'Due {0}',
  cardEnding: 'ending {0}',
  receiptIssuedBy: 'Issued by',
  customerAddress: 'Customer address',
  customerTaxId: 'Customer ID or company number',
  paymentsTitle: 'How the money arrived',
  paymentsNote: 'A receipt has to state how the money arrived. A cheque needs its bank, branch, account, number and due date.',
  addPayment: 'Add a payment method',
  removePayment: 'Remove',
  bankNumber: 'Bank',
  branchNumber: 'Branch',
  accountNumber: 'Account',
  chequeNumber: 'Cheque number',
  chequeDueDateField: 'Due date',
  cardBrand: 'Card issuer',
  cardLast4: 'Last 4 digits',
  paymentAmount: 'Amount',
  paymentsBalanceHint: 'The payment lines have to add up to the receipt total.',
  issueDateIsToday: 'A document is dated the day it is produced. The day the money arrived is recorded separately.',

  /* Israeli compliance: business settings */
  businessTypeLabel: 'Business type',
  businessTypeOsekPatur: 'Osek Patur (VAT-exempt)',
  businessTypeOsekMurshe: 'Osek Murshe (VAT-registered)',
  businessTypeCompany: 'Limited company',
  businessTypeAmuta: 'Registered non-profit',
  businessTypeNote: 'An Osek Patur issues a receipt and charges no VAT. An Osek Murshe or a company issues a tax invoice / receipt, and VAT is carved out of what the student pays rather than added on top.',
  registrationNumberHelp: 'Nine digits including the check digit. It appears on every document.',
  addressStreet: 'Street',
  addressHouse: 'House number',
  addressCity: 'City',
  addressZip: 'Post code',
  addressPartsNote: 'The address appears on the document, and the Tax Authority export needs street, house and city apart.',
  softwareRegistryNumber: 'Software registration number',
  softwareRegistryHelp: 'The number the Tax Authority issues when the software is entered in its registry. It appears on every document and in the uniform-structure file.',
  numberingLockedNote: 'A numbered document has been issued, so the numbering can no longer be changed. A serial run has to stay continuous through the tax year.',
  complianceTitle: 'Legal readiness',
  complianceReady: 'Everything the law requires is filled in. Documents issued from here meet the bookkeeping rules.',
  complianceMissing: 'Still missing, and required on every document:',
  complianceWillIssue: 'Documents will be issued as: {0}',
  complianceVatRate: 'Current VAT rate: {0}%',
  complianceFieldBusinessName: 'Business name',
  complianceFieldRegistrationNumber: 'Registration number',
  complianceFieldAddress: 'Business address',
  complianceFieldSoftwareRegistryNumber: 'Software registration number',

  /* Israeli compliance: the uniform-structure export */
  openFormatTitle: 'OPEN FORMAT',
  openFormatBody: 'Produces the Tax Authority uniform-structure files, INI.TXT and BKMVDATA.TXT, for the period you choose. This is the file an auditor asks for.',
  openFormatMoved: 'OPEN FORMAT has a screen of its own, one step from the main menu.',
  openFormatOpen: 'Go to OPEN FORMAT',
  openFormatActivate: 'Run OPEN FORMAT',
  openFormatRunAgain: 'Run again',
  openFormatDialogBusiness: 'Business being audited',
  openFormatDialogRange: 'Date range',
  openFormatDialogDestination: 'Where the files are saved',
  openFormatDialogDestinationBody: 'The files are saved to the browser\'s download folder, in an archive named OPENFRMT.zip. A browser does not expose the drives on the computer, so there is no drive to choose here. Inside the archive the files sit in the folder structure the instructions require:',
  openFormatDialogRun: 'Produce the files',
  openFormatRunning: 'Producing...',
  openFormatRunVatId: 'Registration number',
  openFormatRunBusiness: 'Business name',
  openFormatRunDone: 'OPEN FORMAT completed successfully.',
  openFormatRunPath: 'The data was saved under this path:',
  openFormatRunPathNote: 'Inside the archive OPENFRMT.zip, in the browser\'s download folder.',
  openFormatRunRange: 'Date range: from {0} to {1}',
  openFormatRunRecordsTitle: 'Record types created in BKMVDATA.TXT',
  openFormatRunCode: 'Record code',
  openFormatRunDescription: 'Record description',
  openFormatRunCount: 'Records',
  openFormatRecordA100: 'Opening record',
  openFormatRecordB100: 'Ledger movements',
  openFormatRecordB110: 'Ledger account',
  openFormatRecordC100: 'Document header',
  openFormatRecordD110: 'Document lines',
  openFormatRecordD120: 'Receipt details',
  openFormatRecordM100: 'Stock item',
  openFormatRecordZ900: 'Closing record',
  openFormatRunSoftware: 'The data was produced by {0}, registration certificate number {1}, on {2} at {3}',
  openFormatRunNoRegistry: 'not yet issued',
  openFormatRangeError: 'Choose a valid date range.',
  openFormatSummaryTitle: 'Document counts report',
  openFormatSummaryBody: 'A count and a total for every document type in appendix 1 of the uniform-structure instructions, over the same period. A type the software does not manage shows a zero. This is the report an auditor holds beside the file.',
  openFormatSummaryShow: 'Show the report',
  openFormatSummaryPrint: 'Print',
  openFormatSummaryCode: 'Code',
  openFormatSummaryDocument: 'Document type',
  openFormatSummaryCount: 'Count',
  openFormatSummaryTotal: 'Total',
  openFormatSummaryCancelled: 'of which cancelled',
  openFormatSummaryRecords: 'Records in the file',
  openFormatSummaryPeriod: 'Period',

  /* Israeli compliance: what went wrong */
  errorNumberingLocked: 'Numbering cannot change once a numbered document has been issued.',
  errorRegistrationNumberRequired: 'A VAT-registered business needs its registration number before it can issue a document.',
  errorInvalidRegistrationNumber: 'That registration number is not valid. Check the check digit.',
  errorInvalidCustomerTaxId: 'That customer ID number is not valid.',
  errorPaymentsDoNotBalance: 'The payment lines do not add up to the receipt total.',
  errorPaymentDetailsRequired: 'Payment details the law requires are missing.',

  /* Business settings */
  businessTitle: 'Business details',
  businessSnapshotNote: 'Changing anything here does not change a receipt that has already been issued. Issuing freezes a snapshot of these fields, so an official document cannot change after the fact.',
  businessDetails: 'Business details',
  businessName: 'Business name',
  registrationNumber: 'Registration number',
  email: 'Email',
  website: 'Website',
  numberingTitle: 'Receipt numbering',
  numberPrefix: 'Prefix',
  numberStart: 'Starting number',
  numberLength: 'Number length',
  yearPrefix: 'Include the year in the number',
  perYearNumbering: 'A separate counter per tax year',
  nextNumberPreview: 'The next number will look like this:',
  documentTitle: 'The document',
  signatureName: 'Signature name',
  printSize: 'Print size',
  printSizeReceipt: 'Receipt roll',
  footerText: 'Footer text',
  saved: 'Saved',

  /* Receipt reports */
  noIssuedReceipts: 'No receipts issued yet',
  noIssuedReceiptsHint: 'The reports fill in once the first receipt is issued',
  receiptsCount: 'Count',
  issuedCount: 'Receipts issued',
  issuedTotal: 'Total issued',
  averageReceipt: 'Average receipt',
  sequenceTitle: 'Number sequence',
  sequenceComplete: 'The sequence is complete, with no missing numbers.',
  sequenceGaps: 'Missing numbers:',
  byMonth: 'By month',
  byStudent: 'By customer',
  byMethod: 'By payment method',

  /* Admin */
  adminOnly: 'This screen is for administrators',
  adminOnlyHint: 'This account has no admin permission',
  adminSearchPlaceholder: 'Search by uid, email or name',
  adminNoUsers: 'No users found',
  adminNoUsersHint: 'Search by full uid, or by part of an email address',
  adminMonths: 'Months of premium',
  adminMonthsHint: 'Grant and Renew add this many months to the end of the current period, or from today when nothing is active. With the months box empty the date is used, and with no date there is no end.',
  adminExpiresAt: 'Valid until',
  adminNote: 'Note',
  adminGrant: 'Grant premium',
  adminTrial: 'Start a trial',
  adminCancel: 'Cancel subscription',
  adminRevoke: 'Revoke premium',
  adminExpire: 'Mark expired',
  adminRenew: 'Renew',
  adminNotes: 'Save note',
  adminAudit: 'Audit log',
  adminNoAudit: 'Nothing logged yet',
  adminGrantedManually: 'Granted manually',

  /* Monthly report */
  reportMonth: 'Month',
  reportBilled: 'Billed',
  reportSummaryTitle: 'How the month closed',
  reportLessons: 'Lessons',
  reportCollectionRate: 'Collection rate',
  reportActiveStudents: 'Active students',
  reportVsPrevious: 'Against last month',
  reportMissingReceipts: 'Income with no receipt',
  reportByStudent: 'By student',
  reportByWeekday: 'By weekday',
  reportInactive: 'No lessons this month',
  noReports: 'No reports yet',
  noReportsHint: 'A report is written at the end of each month that had lessons',

  /* Student page */
  rhythmTitle: 'Rhythm',
  lessonsDone: 'Lessons given',
  attendanceRate: 'Attendance',
  daysSinceLast: 'Days since the last lesson',
  lessonsPerMonth: 'Lessons per month',
  freeCancels: 'Cancelled, not charged',
  chargedCancels: 'Cancelled, charged',
  monthlyTrend: 'Across the year',
  recurringHours: 'Recurring hours',
  availabilityTitle: 'Availability',
  contactsTitle: 'Contacts',

  /* Public documents */
  howToPay: 'How to pay',
  lessonsInThisRequest: 'Lessons in this request',
  from: 'from',
  until: 'until',
  contactName: 'Contact name',
  contactPhone: 'Contact phone',
  send: 'Send',

  /* Reminders and intake links */
  navReminders: 'Payment reminders',
  navIntakeForms: 'Joining links',
  sendReminder: 'Send a reminder',
  sendReminderShort: 'Send',
  sendReminderWhatsApp: 'Send a reminder on WhatsApp',
  copyReminderShort: 'Copy',
  copyReminderNoPhone: 'Copy the reminder message, no phone number is saved',
  reminderCopied: 'Copied',
  reminderLockedTitle: 'Payment reminders are available with Premium',
  chooseReminderRecipient: 'Send a reminder',
  chooseReminderRecipientHint: 'Choose who should receive the payment message',
  copyReminderPrompt: 'Copy the reminder message:',
  reminderHello: 'Hello {0},',
  reminderIntro: 'Here is the summary of lessons that have not been paid:',
  reminderIntroFor: 'Here is the summary of unpaid lessons for {0}:',
  reminderLessonLine: '{0} · {1} · {2} NIS {3}',
  reminderCancelledNote: '(cancelled and charged)',
  reminderTotal: 'Total to pay: {0} NIS',
  reminderDocumentIntro: 'View the payment request:',
  reminderHowToPay: 'Payment options',
  paybox: 'PayBox',
  bit: 'Bit',
  bankTransfer: 'Bank transfer',
  accountOwner: 'Account owner',
  reminderThanks: 'Thank you',
  reminderSignature: 'Sent with Magbit',
  reminderGreeting: 'Hello, here is the summary for {0}:',
  noDebt: 'Nothing outstanding',
  noDebtHint: 'A lesson becomes a debt only once it has ended',
  noPhone: 'No phone number saved',
  newIntakeForm: 'Create a link',
  noIntakeForms: 'No links yet',
  noIntakeFormsHint: 'Send a link to a client so they fill in their own details',
  formOpen: 'Open',
  formClosed: 'Closed',
  revokeForm: 'Close the link',
  revokeFormBody: 'The link stops working. Cards that already arrived through it stay.',
  create: 'Create',
  undo: 'Undo',
  lessonDeleted: "{0}'s lesson was deleted",
  markedPaidCount: '{0} lesson marked as paid || {0} lessons marked as paid',
  studentQuietWarning: '{0} day since the last lesson, and nothing booked || {0} days since the last lesson, and nothing booked',
  lessonsByWeekday: 'Lessons by weekday',
  fromHour: 'From',
  toHour: 'Until',
  checkoutFailed: 'The checkout could not be opened. Please try again.',
  shareOnWhatsApp: 'Share on WhatsApp',
  intakeShareMessage: 'You can fill in your details here:',
  intakeLabel: 'Link name',
  intakeLabelHint: 'For you only, to tell links apart',
  intakeNote: 'Note for whoever fills it in',
  intakeNoteHint: 'Shown at the top of the form to anyone opening the link',
  formSubmissions: '{0} card received || {0} cards received',
  intakeReceivedTitle: 'Cards received',
  intakeReceivedNone: 'Nothing has come in through the links yet',

  /* Landing page */
  heroTitle: 'Magbit',
  heroSub: 'Keeps students, payments and the lesson board in one place.',
  ctaButton: 'Sign in with Google',
  moneyTitle: 'Who owes you, what came in, and what was cancelled.',
  moneyCopy: 'Every lesson carries its own status: paid, outstanding, cancelled and charged, or cancelled and not charged. On a student card: what they owe. In the monthly picture: what came in, and who brings the most.',
  moneyCaption: 'The student list, with what each one owes.',
  moneyShotAlt: 'The Magbit student list, with an outstanding column for each student',
  syncTitle: 'Your lessons are already in Google Calendar.',
  syncRule1: 'When Magbit and the calendar disagree, the calendar wins.',
  syncRule2: 'Automatic sync never deletes a lesson on its own.',
  syncRule3: 'A change, cancellation or deletion in Magbit updates the calendar automatically.',
  seeItTitle: 'This is what it looks like.',
  seeItCaption: 'The weekly lesson board, live.',
  seeItShotAlt: 'The weekly Magbit lesson board, with lessons marked paid, unpaid and cancelled-charged',
  premiumEyebrow: 'Magbit Premium',
  pmTitle: 'Receipts, without having to remember.',
  pmFeature1Title: 'A receipt written for you',
  pmFeature1Text: 'A digital receipt with a running serial is written for every lesson that was paid. If a paid lesson still has no receipt, you are told, so no income is left undocumented.',
  pmFeature2Title: 'Reports and exports',
  pmFeature2Text: 'Monthly income reports, and exports to CSV and Excel.',
  premiumCtaTrial: 'Start a 7 day trial',
  premiumCtaPricing: 'See the full pricing',
  faqEyebrow: 'Common questions',
  faqTitle: 'Before you start.',
  faq1Q: 'Do I need a card to begin?',
  faq1A: 'Not to sign up. A card is needed only when a Premium trial starts, with no charge until the 7 days are up, and it can be cancelled before then at no cost.',
  faq2Q: 'How does the Google Calendar sync work?',
  faq2A: 'Both ways: lessons go into the calendar, and calendar changes come back to Magbit. When they disagree, the calendar wins. Nothing is deleted automatically; that happens only in a manual sync, and only after you confirm.',
  faq3Q: 'Is my data safe, and can I export it?',
  faq3A: 'Yes. Signing in goes through Google, and every student and payment belongs only to the signed-in account, fully separated from other users. A full export is available at any time, including income reports in CSV and Excel on the Premium plan.',
  faq4Q: 'What happens if I cancel Premium?',
  faq4A: 'You can cancel at any time from the subscription page, and keep access until the end of the period you paid for. The first charge is fully refundable within 14 days.',
  ctaTitle: 'Start without a credit card.',
  ctaCopy: 'Sign in with Google, and add your first student in a minute.',

  /* Pricing */
  pricingHeadline: 'Less admin. More time to teach.',
  pricingLede: 'Managing students, lessons and payments starts free. When your business needs receipts, reminders and reports that run themselves, Premium handles those too. Includes a {0} day free trial.',
  pricingFlowLabel: 'From lesson to receipt',
  pricingFlowLesson: 'Lesson',
  pricingFlowPayment: 'Payment',
  pricingFlowReceipt: 'Receipt',
  planFreeKicker: 'For everyday management',
  planFreeName: 'Free',
  planFreePrice: 'Free',
  planForever: 'forever',
  planFreeSub: 'Everything you need to run lessons and payments.',
  planFreeCta: 'Start free',
  planFree1: 'Unlimited students and prices',
  planFree2: 'Lessons, payments and outstanding balances',
  planFree3: 'A monthly and weekly calendar',
  planFree4: 'Two-way Google Calendar sync',
  planFree5: 'Charts and income insight',
  pricingPremiumBadge: 'Built for an active business',
  planPremiumSub: 'The admin gets done on time, even when the day is full of lessons.',
  planPremium1: 'Everything included in the Free plan',
  planPremium2: 'Automatic digital receipts with continuous numbering',
  planPremium3: 'Combined receipts and print-ready documents',
  planPremium4: 'Payment reminders and WhatsApp payment requests',
  planPremium5: 'Alerts for income that still needs a receipt',
  planPremium6: 'Monthly income reports, with CSV and Excel exports',
  planPremium7: 'Complete student profiles and client intake forms',
  planPremium8: 'Business branding on documents and full data backup',
  pricingNotesTitle: 'No billing surprises',
  pricingNote1: 'Prices include VAT. Billing is handled by Paddle.',
  pricingNote2: 'The trial starts with no charge, and can be cancelled before it ends at no cost.',
  pricingNote3: 'The first charge is fully refundable within 14 days.',
  pricingBillingLabel: 'Billing',
  pricingBillingMonthly: 'Monthly',
  pricingBillingYearly: 'Yearly',
  pricingAnnualSaving: 'Save nearly two months',
  pricingPerMonth: 'per month',
  pricingPerYear: 'per year',
  pricingAlternatePrefix: 'or',
  pricingSubscribeCta: 'Start free trial',
  pricingTrialNote: '7 days free of charge. Cancel at any time.',
  pricingCheckoutUnavailable: 'Online checkout is temporarily unavailable. Prices and features remain here for reference.',
  welcomeTitle: 'You are all set.',
  welcomeLede: 'Thank you for subscribing. Your receipt is on its way by email, and Premium switches on within a few minutes.',
  welcomeNote: 'If a Premium screen still looks locked when you get there, give it a minute and reload; billing confirmations arrive in the background.',
  welcomeCta: 'Go to the app',

  /* Backup and contacts */
  navContacts: 'Contacts',
  exportTitle: 'Export',
  exportHint: 'Your data, in a file you can keep anywhere.',
  exportAll: 'Export everything',
  exportCards: 'Export student cards',
  importTitle: 'Import',
  importHint: 'Import a full backup or student cards from a file exported by Magbit.',
  importMerge: 'Add what is missing',
  importReplace: 'Make the roster match the file',
  importMergeBody: '{0} card will be added. An existing card is left alone. || {0} cards will be added. An existing card is left alone.',
  importReplaceBody: 'This removes {0} students and the {1} lessons that belong to them. It cannot be undone.',
  importDone: '{0} added, {1} updated, {2} removed',
  fullImportBody: 'The file contains {0} students and {1} lessons. Importing replaces all current data: {2} students and {3} lessons. This action cannot be undone.',
  fullImportDone: 'Backup restored: {0} students and {1} lessons',
  noContacts: 'No contacts yet',
  addContact: 'Add a contact',
  relation: 'Relation',
  receivesPayments: 'Receives payment messages',

  /* Google Calendar */
  navGoogleCalendar: 'Google Calendar',
  gcalConnection: 'Connection',
  gcalConnected: 'Connected',
  gcalNotConnected: 'The calendar is not connected yet.',
  gcalConnect: 'Connect Google Calendar',
  gcalDisconnect: 'Disconnect',
  gcalSyncTitle: 'Sync',
  gcalPush: 'Send {0} lesson to the calendar || Send {0} lessons to the calendar',
  gcalPull: 'Pull changes from the calendar',
  gcalSyncDone: '{0} lesson updated || {0} lessons updated',
  gcalMissing: '{0} lesson was not found in the calendar and was left alone || {0} lessons were not found in the calendar and were left alone',
  importFromGoogle: 'Import from Google',
  importSelected: 'Import {0}',
  noGoogleContacts: 'No contacts found',

  /* Ensembles: groups that learn together */
  navEnsembles: 'Ensembles',
  addEnsemble: 'Add ensemble',
  editEnsembleTitle: 'Edit ensemble',
  noEnsembles: 'No ensembles yet',
  noEnsemblesHint: 'An ensemble is a group that learns together: an orchestra, a chamber group, a theory class. One meeting is recorded as a lesson for each member who is charged.',
  noEnsemblesMatch: 'No ensemble matches that search',
  ensembleName: 'Ensemble name',
  ensembleNameEn: 'Ensemble name in English',
  ensembleMembers: 'Members',
  ensembleMemberCount: '{0} member || {0} members',
  ensembleNoMembers: 'No members yet',
  ensembleWeeklyHour: 'Weekly hour',
  ensembleDuration: 'Meeting length in minutes',
  billingLabel: 'Billing',
  billingPerStudent: 'Each member pays their own price',
  billingSplit: 'Group price, split evenly',
  billingSinglePayer: 'One member pays for the group',
  billingInstitution: 'An organization pays',
  billingInstitutionHint: 'The organization is charged for the meeting and no member is charged at all.',
  groupPrice: 'Group price',
  payerLabel: 'Paid by',
  payingOrganization: 'Paying organization',
  selectPlaceholder: 'Choose',
  noOrganizations: 'No organizations yet. One can be added from the students screen.',
  scheduleMeeting: 'Schedule a meeting',
  meetingCharges: 'What this meeting charges',
  meetingBooked: '{0} lesson booked || {0} lessons booked',
  meetingUnbilled: '{0} member is not charged for this meeting || {0} members are not charged for this meeting',
  meetingNobody: 'There is nobody to charge for this meeting',
  deleteEnsembleTitle: 'Delete {0}',
  deleteEnsembleBody: 'The ensemble is deleted. The lessons it already produced stay with the students, because that is income already earned.',
  groupLesson: 'Group lesson',
  ensembleOf: 'Ensemble: {0}',

  /* A second spelling, and who is taught versus who pays */
  firstNameEn: 'First name in English',
  lastNameEn: 'Last name in English',
  addressEn: 'Address in English',
  notesEn: 'Notes in English',
  contactNameEn: 'Contact name in English',
  relationEn: 'Relation in English',
  spellingHint: 'The English spelling is shown while the interface is in English. The original name stays the identity of the card.',
  kindLabel: 'Card type',
  kindStudent: 'Student',
  kindOrganization: 'Organization',
  kindOrganizationHint: 'An organization pays for an ensemble without being taught. It is not counted among the students, but it has debt, reminders and receipts exactly like one.',
  kindFilterLabel: 'Students or organizations',
  kindFilterTaught: 'Students',
  kindFilterOrganizations: 'Organizations',
  organizationBadge: 'Organization',
  noOrganizationsYet: 'No organizations yet',
  noOrganizationsHint: 'An organization is a body that pays for an ensemble: a conservatory, a music programme, a community centre.'
};
